import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Table2,
  RefreshCw,
  Percent,
  Wand2,
  XCircle,
  Info,
  Ship,
  Truck,
  Download,
  CalendarClock,
  History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import axiosInstance from '@/lib/api/axios-instance';
import { tradeService } from '../services/trade-service';
import { RatesTable } from '../components/rates-table';
import { MarginSettings } from '../components/margin-settings';
import { TarifarioHistorial } from '../components/tarifario-historial';
import type { TariffStatus, TarifarioImportResult } from '../types/trade.types';
import { groupMismatches } from '../utils/group-mismatches';
import { formatFullDate } from '../utils/trade-dates';

/**
 * Tarifario.
 *
 * Es la pantalla de Delicia: acá sube el Excel de la semana y ve el estado de
 * vigencia. La importación va en **dos pasos** a propósito — primero el reporte,
 * después la confirmación — porque importar un tarifario cambia el precio de
 * todas las cotizaciones nuevas.
 */

export function TarifarioPage() {
  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <TarifarioContent />
      </div>
    </AppLayout>
  );
}

function TarifarioContent() {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<TariffStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState<TarifarioImportResult | null>(null);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Sube tras cada importación confirmada, para que la tabla de tarifas relea. */
  const [ratesVersion, setRatesVersion] = useState(0);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      setStatus(await tradeService.getTariffStatus());
    } catch {
      setStatus(null);
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  /** Paso 1: leer y reportar, sin escribir. */
  const analyze = useCallback(async (chosen: File) => {
    setError(null);
    setReport(null);
    setFile(chosen);
    setAnalyzing(true);
    try {
      setReport(await tradeService.importTarifario(chosen, false));
    } catch (err) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'No se pudo leer el tarifario.'
      );
    } finally {
      setAnalyzing(false);
    }
  }, []);

  /** Paso 2: escribir. Solo después de que la persona vio el reporte. */
  const confirmImport = useCallback(async () => {
    if (!file) return;
    setImporting(true);
    try {
      const result = await tradeService.importTarifario(file, true);
      setReport(result);
      /*
        La tabla de abajo tiene que volver a leer.

        Importar REEMPLAZA las tarifas —borra y recrea—, así que los ids que la
        tabla tenía en memoria ya no existen. Sin esto, editar una fila recién
        importada devolvía "la tarifa no existe" hasta recargar la página a mano.
      */
      setRatesVersion((v) => v + 1);
      const total = result.sheets.reduce((sum, s) => sum + s.imported, 0);

      /*
        Cero tarifas NO es un éxito, y decir "0 tarifas cargadas, ya se puede
        cotizar" en verde es peor que no decir nada: el usuario se va convencido
        de que terminó.

        Pasa de verdad. Le ocurrió a una empresa recién creada: no tenía tipos de
        contenedor dados de alta, así que todas las filas se rechazaron por
        "equipo desconocido" y el aviso decía éxito. El motivo real está en los
        avisos del reporte, así que se muestra ese.
      */
      if (total === 0) {
        toast({
          title: 'No se importó ninguna tarifa',
          description:
            result.warnings[0] ??
            'Ninguna fila se pudo guardar. El reporte de abajo dice el motivo de cada rechazo.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Tarifario importado',
          description: `${total} tarifas cargadas. Ya se puede cotizar con ellas.`,
          variant: 'success',
        });
      }
      void loadStatus();
    } catch (err) {
      toast({
        title: 'No se pudo importar',
        description:
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Ocurrió un error al importar.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  }, [file, toast, loadStatus]);

  /**
   * Baja la plantilla del tarifario.
   *
   * Por el cliente HTTP y no con un enlace directo: el endpoint pide el token del
   * usuario, y un `href` a la URL no lleva la cabecera de autorización. Mismo
   * camino que usa el PDF de la cotización.
   */
  const downloadTemplate = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/api/trade/tarifario/plantilla', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla-tarifario-prixia.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'No se pudo descargar la plantilla',
        description: 'Reintentá en un momento.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const reset = () => {
    setFile(null);
    setReport(null);
    setError(null);
  };

  const totalImported = report?.sheets.reduce((sum, s) => sum + s.imported, 0) ?? 0;
  const totalRead = report?.sheets.reduce((sum, s) => sum + s.read, 0) ?? 0;

  return (
    <div className="space-y-6">
      {/*
        El encabezado de Comercia (artefacto, 19-oct), con el estado del
        tarifario a la derecha. Antes era una franja azul oscuro que en modo
        oscuro quedaba como un agujero.
      */}
      <PageHeader
        etiqueta="Datos Maestros"
        tonoEtiqueta="neutro"
        icono={Table2}
        titulo="Tarifario"
        subtitulo="Las tarifas negociadas con navieras y transportistas. Se cargan del Excel y se editan acá."
        acciones={
          status && (
            <dl className="flex gap-2">
              <Stat label="Vigentes" value={status.validRates} />
              <Stat
                label="Vencidas"
                value={status.expiredRates}
                tone={status.expiredRates > 0 ? 'warn' : 'ok'}
              />
              <Stat label="Total" value={status.totalRates} />
            </dl>
          )
        }
      />

      {/*
        Dos avisos distintos, y el orden importa.

        El de "por vencer" va PRIMERO y en azul: es el accionable. Todavía hay
        tiempo de pedirle la renovación al proveedor.

        El de "ya vencieron" va después y en ámbar: informa un problema que ya
        ocurrió. Cuando aparece, el vendedor ya está cotizando con tarifas muertas.
      */}
      {!loadingStatus && status && status.expiringSoon > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-info-borde bg-info-suave px-4 py-3">
          <p className="flex items-start gap-2 text-sm leading-snug text-foreground">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-info" />
            <span>
              <span className="font-semibold">
                {status.expiringSoon}{' '}
                {status.expiringSoon === 1 ? 'tarifa vence' : 'tarifas vencen'} en los próximos{' '}
                {status.expiryWarningDays} días
                {status.nextExpiry ? `, la primera el ${formatFullDate(status.nextExpiry)}` : ''}.
              </span>{' '}
              Es buen momento para pedir la renovación, antes de tener que cotizar con vencidas.
            </span>
          </p>
        </div>
      )}

      {!loadingStatus && status && status.expiredRates > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-alerta-borde bg-alerta-suave px-4 py-3">
          <p className="flex items-start gap-2 text-sm leading-snug text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alerta" />
            <span>
              <span className="font-semibold">
                {status.expiredRates} tarifas vencieron
                {status.lastExpiry ? ` el ${formatFullDate(status.lastExpiry)}` : ''}.
              </span>{' '}
              El cotizador las sigue usando si no hay otra, pero las marca — y siempre prefiere una
              vigente aunque sea más cara.
            </span>
          </p>
        </div>
      )}

      {/* ── Importar ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">Cargar el tarifario de la semana</CardTitle>
              <CardDescription className="text-xs">
                El mismo Excel de siempre. Primero se muestra qué entra y qué se rechaza; nada se
                guarda hasta que confirmes.
              </CardDescription>
            </div>
            {status && (
              <Button size="sm" variant="ghost" onClick={() => void loadStatus()}>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Actualizar
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {!report && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                if (!analyzing) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const dropped = e.dataTransfer.files[0];
                if (dropped && !analyzing) void analyze(dropped);
              }}
              className={cn(
                'rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors',
                dragging ? 'border-prixia-blue bg-prixia-sky/10' : 'border-border'
              )}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                className="sr-only"
                onChange={(e) => {
                  const chosen = e.target.files?.[0];
                  if (chosen) void analyze(chosen);
                  e.target.value = '';
                }}
              />

              {analyzing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-7 w-7 animate-spin text-prixia-blue" />
                  <p className="text-sm font-medium">Leyendo {file?.name}…</p>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto h-8 w-8 text-prixia-silver" />
                  <p className="mt-2 text-sm font-medium">Arrastra el Excel del tarifario</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Se leen las hojas de tarifas marítimas y terrestres
                  </p>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
                      Elegir archivo
                    </Button>
                    {/*
                      La plantilla va JUNTO al botón de subir: es el momento en que
                      se necesita. Quien nunca cargó un tarifario no sabe qué
                      columnas hacen falta, y buscarlo en otra pantalla es una
                      barrera para el primer uso.
                    */}
                    <Button variant="ghost" size="sm" onClick={downloadTemplate}>
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Descargar plantilla
                    </Button>
                    {/*
                      El historial va acá, al lado de subir, porque es el momento
                      en que importa: antes de reemplazar el tarifario es cuando
                      uno quiere poder ver qué había. En otra pantalla nadie se
                      acordaría de que existe.
                    */}
                    <Button variant="ghost" size="sm" onClick={() => setHistorialAbierto(true)}>
                      <History className="mr-1.5 h-3.5 w-3.5" />
                      Historial de cargas
                    </Button>
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    ¿Primera vez? Descarga la plantilla, llénala con tus tarifas y súbela.
                  </p>
                </>
              )}
            </div>
          )}

          {error && (
            <p className="flex items-start gap-1.5 rounded-md border border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-xs text-bloqueo">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {error}
            </p>
          )}

          {/* ── El reporte ── */}
          {report && (
            <div className="space-y-4">
              <div
                className={cn(
                  'flex flex-wrap items-center justify-between gap-3 rounded-lg px-4 py-3',
                  report.dryRun ? 'bg-prixia-sky/10' : 'bg-ok-suave'
                )}
              >
                <p className="flex items-center gap-2 text-sm">
                  {report.dryRun ? (
                    <>
                      <Info className="h-4 w-4 text-prixia-blue" />
                      <span>
                        Se leyeron <strong>{totalRead}</strong> tarifas de{' '}
                        <span className="font-mono text-xs">{report.fileName}</span>. Todavía no se
                        guardó nada.
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 text-ok" />
                      <span>
                        <strong>{totalImported}</strong> tarifas importadas. Ya se puede cotizar con
                        ellas.
                      </span>
                    </>
                  )}
                </p>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={reset}>
                    {report.dryRun ? 'Descartar' : 'Cargar otro'}
                  </Button>
                  {report.dryRun && (
                    <Button
                      size="sm"
                      onClick={() => void confirmImport()}
                      disabled={importing || totalRead === 0}
                      variant="degradado"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          Importando…
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                          Confirmar importación
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Por hoja */}
              <div className="grid gap-2 sm:grid-cols-2">
                {report.sheets.map((sheet) => (
                  <div key={sheet.sheet} className="rounded-lg border p-3">
                    <p className="flex items-center gap-1.5 text-xs font-semibold">
                      {sheet.sheet.toLowerCase().includes('marítim') ? (
                        <Ship className="h-3.5 w-3.5 text-prixia-blue" />
                      ) : (
                        <Truck className="h-3.5 w-3.5 text-prixia-blue" />
                      )}
                      {sheet.sheet}
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums">
                      {report.dryRun ? sheet.read : sheet.imported}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        {report.dryRun ? 'leídas' : `de ${sheet.read} importadas`}
                      </span>
                    </p>
                    {sheet.rejected > 0 && (
                      <p className="mt-0.5 text-[11px] text-bloqueo">{sheet.rejected} rechazadas</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Lo que el importador arregló solo */}
              {(report.normalized.length > 0 ||
                report.extractedRestrictions.length > 0 ||
                report.expandedRows > 0) && (
                <section className="rounded-lg border bg-muted/30 p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
                    <Wand2 className="h-3.5 w-3.5 text-prixia-blue" />
                    Lo que se corrigió automáticamente
                  </p>
                  <ul className="space-y-1 text-[11px]">
                    {report.normalized.map((n, i) => (
                      <li key={i}>
                        <span className="text-muted-foreground">{n.from.join(' · ')}</span>
                        <span className="mx-1.5 text-prixia-silver">→</span>
                        <span className="font-medium">{n.to}</span>
                      </li>
                    ))}
                    {report.extractedRestrictions.map((r, i) => (
                      <li key={`r-${i}`}>
                        <span className="font-medium">{r.provider}</span>
                        <span className="text-muted-foreground">
                          {' '}
                          solo mueve carga de {r.requiresCarrier} — se separó del nombre
                        </span>
                      </li>
                    ))}
                    {report.expandedRows > 0 && (
                      <li className="text-muted-foreground">
                        {report.expandedRows} filas que aplicaban a dos equipos se convirtieron en
                        dos tarifas
                      </li>
                    )}
                  </ul>
                </section>
              )}

              {/* Avisos */}
              {report.warnings.length > 0 && (
                <div className="space-y-1 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2">
                  {report.warnings.map((w, i) => (
                    <p
                      key={i}
                      className="flex items-start gap-1.5 text-xs leading-snug text-alerta"
                    >
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      {w}
                    </p>
                  ))}
                </div>
              )}

              {/* Rechazos, fila por fila */}
              {report.rejected.length > 0 && (
                <section className="rounded-lg border border-bloqueo-borde">
                  <p className="flex items-center gap-1.5 border-b border-bloqueo-borde bg-bloqueo-suave px-3 py-2 text-xs font-semibold text-bloqueo">
                    <XCircle className="h-3.5 w-3.5" />
                    {report.rejected.length} filas rechazadas — no se importaron
                  </p>
                  <ul className="max-h-56 divide-y overflow-y-auto">
                    {report.rejected.map((r, i) => (
                      <li key={i} className="px-3 py-1.5 text-[11px]">
                        <span className="font-mono text-muted-foreground">
                          {r.sheet} · fila {r.row}
                        </span>
                        <span className="mx-1.5">—</span>
                        {r.reason}
                        {r.preview && (
                          <span className="ml-1.5 text-muted-foreground">[{r.preview}]</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/*
                Desglose que no cuadra, AGRUPADO por naviera y agente.

                Antes se listaban las 88 filas una por una, y eso no es accionable:
                nadie revisa 88 renglones. Agrupadas son **4 patrones**, y el hueco
                es fijo dentro de cada uno — ONE siempre 300, COSCO 238 y 248, MSC
                40 solo en Iquique. Eso ya es una pregunta que Delicia puede
                contestar y arreglar en la fuente.
              */}
              {report.mismatches.length > 0 && (
                <section className="rounded-lg border">
                  <p className="border-b bg-muted/40 px-3 py-2 text-xs font-semibold">
                    {report.mismatches.length} tarifas entraron pero su desglose no suma el total
                    del Excel
                  </p>

                  <ul className="divide-y">
                    {groupMismatches(report.mismatches).map((g) => (
                      <li
                        key={g.key}
                        className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                      >
                        <span>
                          <span className="font-semibold">{g.carrier}</span>
                          {g.agent && (
                            <span className="text-muted-foreground"> · agente {g.agent}</span>
                          )}
                          <span className="ml-2 text-[11px] text-muted-foreground">
                            {g.rows} {g.rows === 1 ? 'tarifa' : 'tarifas'}
                          </span>
                        </span>
                        <span className="shrink-0 font-mono tabular-nums text-alerta">
                          faltan {g.difference.toFixed(0)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="border-t bg-muted/20 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                    <p>
                      Se importó el total del Excel, que es lo que se cobra. La diferencia aparece
                      en la cotización como una línea calculada.
                    </p>
                    <p className="mt-1">
                      <span className="font-semibold text-foreground">
                        Para preguntarle a quien arma el tarifario:
                      </span>{' '}
                      en estas filas quedan vacías <em>Profit Agente</em>,{' '}
                      <em>Apertura Manifiesto</em> y <em>Emisión BL</em>, pero el Total sí las
                      incluye. ¿Ese monto son esos tres conceptos juntos? Si se separan como en las
                      demás filas, el desglose cierra siempre.
                    </p>
                  </div>
                </section>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Las tarifas, editables ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Table2 className="h-4 w-4 text-prixia-blue" />
            Las tarifas cargadas
          </CardTitle>
          <CardDescription className="text-xs">
            Corrige una tarifa suelta sin tener que reimportar todo el Excel. El proveedor, el tramo
            y el equipo no se editan: cambiar cualquiera de esos sería otra tarifa distinta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RatesTable refreshKey={ratesVersion} />
        </CardContent>
      </Card>

      {/* ── Márgenes ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Percent className="h-4 w-4 text-prixia-blue" />
            Parámetros de ganancia
          </CardTitle>
          <CardDescription className="text-xs">
            El tarifario guarda <strong>costo</strong>. El margen es lo que se le suma para llegar
            al precio que ve el cliente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <MarginSettings />

          <div className="rounded-lg bg-muted/30 p-3 text-[11px] leading-relaxed text-muted-foreground">
            El terrestre arranca en <strong className="text-foreground">0 %</strong> a propósito: la
            hoja del Excel trae <span className="font-mono">Precio USD</span> sin columna de margen,
            y no está confirmado si ya lo incluye. Cobrar de menos se recupera; cobrar el margen dos
            veces cuesta la venta.
          </div>
        </CardContent>
      </Card>

      {/*
        A dónde ir cuando falta un dato de catálogo.

        Los cuatro catálogos del flete —tramos, proveedores, servicios y
        ubicaciones— colgaban del menú del Tarifario y se movieron a Datos
        Maestros, que es donde siempre vivieron sus páginas. El acceso rápido no
        se pierde: se ofrece desde acá, que es donde aparece la necesidad —el
        Excel nombra una naviera o un puerto que todavía no existe.
      */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 text-xs">
          <span className="text-muted-foreground">
            ¿El Excel nombra un proveedor o un puerto que no existe todavía?
          </span>
          {[
            { label: 'Proveedores de flete', href: '/master-data/freight-providers' },
            { label: 'Ubicaciones', href: '/master-data/freight-locations' },
            { label: 'Tramos y tarifas', href: '/master-data/freight-leg-catalog' },
            { label: 'Servicios de flete', href: '/master-data/freight-services' },
          ].map((destino) => (
            <Link
              key={destino.href}
              to={destino.href}
              className="font-medium text-prixia-blue underline-offset-2 hover:underline"
            >
              {destino.label}
            </Link>
          ))}
        </CardContent>
      </Card>

      <TarifarioHistorial open={historialAbierto} onOpenChange={setHistorialAbierto} />
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'ok',
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div
      className={cn(
        'min-w-[88px] rounded-xl border px-3.5 py-2 text-right',
        tone === 'warn' ? 'border-alerta-borde bg-alerta-suave' : 'bg-card'
      )}
    >
      <dd
        className={cn(
          'font-titulo text-xl font-semibold leading-none tabular-nums',
          tone === 'warn' && 'text-alerta'
        )}
      >
        {value}
      </dd>
      <dt className="mt-1 text-xs text-muted-foreground">{label}</dt>
    </div>
  );
}
